import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Mark } from './ui/kit';
import { MARKS } from '../lib/marks';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Show nothing while checking auth — prevents login form flash
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper">
        <svg className="animate-spin h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-paper">
      {/* Left — the ink panel. The one dark surface, as on the marketing site. */}
      <div className="relative hidden overflow-hidden bg-ink lg:flex lg:w-[45%]">
        <div className="absolute inset-0 bg-grid-dark opacity-40" />
        {/*
          `relative` without a z-index on purpose: both layers are positioned, so
          DOM order already puts this on top, and staying at `z-index: auto`
          avoids opening a stacking context. A stacking context here would
          isolate the Mark's `mix-blend-screen` from the ink behind it and the
          drawing would sit in a black box.
        */}
        <div className="relative flex w-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <img src="/foundlio-on-dark.svg" alt="" className="h-9 w-auto" />
            <span className="font-display text-xl font-bold tracking-tight">Foundlio</span>
          </div>

          <div className="max-w-md">
            {/* The drawing rides the ink, so it needs the inverted treatment. */}
            <Mark src={MARKS.signIn} size="xl" tone="dark" className="-ml-3 mb-8" />
            <div className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
              Client portal
            </div>
            <h1 className="mt-5 font-display text-[2.5rem] font-bold leading-[1.05] tracking-[-0.03em]">
              Your website,
              <br />
              <span className="text-zinc-500">and everything it brings in.</span>
            </h1>
            <p className="mt-6 text-[15px] leading-relaxed text-zinc-400">
              Leads, bookings, reviews and traffic for your site — in one place, updated as they
              happen.
            </p>
          </div>

          <p className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-600">
            &copy; {new Date().getFullYear()} Foundlio
          </p>
        </div>
      </div>

      {/* Right — the form, on paper */}
      <div className="fx-paper flex flex-1 items-center justify-center p-8">
        <div className="relative z-[1] w-full max-w-sm">
          {/* Mobile masthead — stands in for the ink panel, which is hidden here. */}
          <div className="mb-10 lg:hidden">
            {/*
              The `z-[1]` wrapper above (needed to clear .fx-paper's grain layer)
              is a stacking context, so the Mark's multiply has no backdrop to
              eat and its white ground would show as a box. Painting the paper
              colour inside the group gives the blend something to land on.
            */}
            <div className="bg-paper">
              <Mark src={MARKS.signIn} size="lg" className="mx-auto" />
            </div>
            <div className="mt-4 flex items-center justify-center gap-2.5">
              <img src="/foundlio-on-light.svg" alt="" className="h-8 w-auto" />
              <span className="font-display text-xl font-bold tracking-tight text-primary">Foundlio</span>
            </div>
          </div>

          <div className="mb-8">
            <div className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500">
              Sign in
            </div>
            <h2 className="mt-3 font-display text-[2rem] font-bold leading-[1.1] tracking-[-0.03em] text-primary">
              Welcome back
            </h2>
            <div className="mt-5 h-[3px] w-12 rounded-full bg-primary" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <label
                htmlFor="email"
                className="block font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500"
              >
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="password"
                className="block font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500"
              >
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input-field"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-sm font-medium text-white transition-transform duration-200 hover:scale-[1.02] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <span aria-hidden>→</span>
                </>
              )}
            </button>

            <p className="pt-4 text-center text-[13px] text-muted">
              Don&apos;t have an account?{' '}
              <a
                href="https://foundlio.com/contact"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Contact us
              </a>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
